import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {AuthContext} from '../auth/ContextAuthentication';

const capabilityLabel = (capability) =>
  capability
    .replace(/[:._-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const AdminManagement = () => {
  const {accessToken, adminSession} = useContext(AuthContext);
  const [admins, setAdmins] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [resetTargetId, setResetTargetId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [actorPassword, setActorPassword] = useState('');
  const [message, setMessage] = useState('');

  const headers = useMemo(() => ({Authorization: `Bearer ${accessToken}`}), [accessToken]);

  const clearResetForm = () => {
    setResetTargetId(null);
    setNewPassword('');
    setConfirmPassword('');
    setActorPassword('');
  };

  const load = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const [adminsResponse, capabilitiesResponse] = await Promise.all([
        axios.get('api/admin/capability-management/admins', {
          baseURL: process.env.REACT_APP_API_URL,
          headers,
          timeout: 10000,
        }),
        axios.get('api/admin/capability-management/capabilities', {
          baseURL: process.env.REACT_APP_API_URL,
          headers,
          timeout: 10000,
        }),
      ]);
      setAdmins(adminsResponse?.data?.admins || []);
      setCapabilities(capabilitiesResponse?.data?.capabilities || []);
    } catch (_error) {
      setMessage('Unable to load administrator capabilities.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (accessToken && adminSession?.role === 'Super Admin') {
      load();
    } else {
      setLoading(false);
    }
  }, [accessToken, adminSession?.role, load]);

  const toggle = (adminId, capability) => {
    setAdmins((current) =>
      current.map((admin) => {
        if (admin.id !== adminId) return admin;
        const assigned = new Set(admin.assignedCapabilities || []);
        if (assigned.has(capability)) assigned.delete(capability);
        else assigned.add(capability);
        return {...admin, assignedCapabilities: [...assigned]};
      })
    );
  };

  const save = async (admin) => {
    setSavingId(admin.id);
    setMessage('');
    try {
      const response = await axios.put(
        `api/admin/capability-management/admins/${admin.id}`,
        {capabilities: admin.assignedCapabilities || []},
        {
          baseURL: process.env.REACT_APP_API_URL,
          headers,
          timeout: 10000,
        }
      );
      const updated = response?.data?.admin;
      setAdmins((current) =>
        current.map((item) => (item.id === updated?.id ? updated : item))
      );
      setMessage('Capabilities saved. The administrator must refresh their session.');
    } catch (_error) {
      setMessage('Unable to save capabilities.');
    } finally {
      setSavingId(null);
    }
  };

  const startReset = (adminId) => {
    setMessage('');
    setResetTargetId(adminId);
    setNewPassword('');
    setConfirmPassword('');
    setActorPassword('');
  };

  const resetPassword = async (admin) => {
    setMessage('');
    if (!actorPassword || !newPassword || !confirmPassword) {
      setMessage('Enter your Super Admin password and the new administrator password.');
      return;
    }
    if (newPassword.length < 12) {
      setMessage('The new administrator password must be at least 12 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('The administrator password confirmation does not match.');
      return;
    }

    setResettingId(admin.id);
    try {
      const response = await axios.put(
        `api/admin/capability-management/admins/${admin.id}/password`,
        {actorPassword, password: newPassword},
        {
          baseURL: process.env.REACT_APP_API_URL,
          headers,
          timeout: 10000,
        }
      );

      if (response?.data?.status !== true) {
        throw new Error(response?.data?.message || 'Password reset failed');
      }

      setMessage(`Password reset for ${admin.email}. Existing sessions were revoked.`);
      clearResetForm();
    } catch (requestError) {
      setMessage(requestError?.response?.data?.message || requestError?.message || 'Unable to reset administrator password.');
      setActorPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setResettingId(null);
    }
  };

  if (adminSession?.role !== 'Super Admin') {
    return <div className="rounded bg-white p-6 shadow">Super Admin access required.</div>;
  }

  if (loading) return <div className="p-6">Loading administrators…</div>;

  return (
    <div className="space-y-4">
      <div className="rounded bg-white p-5 shadow">
        <h1 className="text-2xl font-semibold">Administrator Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Assign least-privilege capabilities or perform a reviewed password reset for a standard administrator.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Password resets require Super Admin re-authentication and revoke the target administrator&apos;s existing sessions.
        </p>
        {message && <p role="status" className="mt-3 text-sm font-medium">{message}</p>}
      </div>

      {admins.length === 0 && (
        <div className="rounded bg-white p-5 shadow">No standard administrators found.</div>
      )}

      {admins.map((admin) => (
        <div key={admin.id} className="rounded bg-white p-5 shadow">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">{admin.fullName || admin.email}</h2>
              <p className="text-sm text-gray-600">{admin.email}</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded border border-[#0C7240] px-4 py-2 text-[#0C7240] disabled:opacity-50"
                disabled={Boolean(resettingId)}
                onClick={() => (resetTargetId === admin.id ? clearResetForm() : startReset(admin.id))}>
                {resetTargetId === admin.id ? 'Cancel reset' : 'Reset password'}
              </button>
              <button
                type="button"
                className="rounded bg-[#0C7240] px-4 py-2 text-white disabled:opacity-50"
                disabled={savingId === admin.id}
                onClick={() => save(admin)}>
                {savingId === admin.id ? 'Saving…' : 'Save capabilities'}
              </button>
            </div>
          </div>

          {resetTargetId === admin.id && (
            <div className="mb-5 rounded border border-amber-300 bg-amber-50 p-4">
              <h3 className="font-semibold text-gray-900">Reviewed password reset</h3>
              <p className="mt-1 text-sm text-gray-700">
                Enter a new password for this administrator and your own Super Admin password to authorize the reset.
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="New administrator password"
                  minLength={12}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="rounded border bg-white px-3 py-2"
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  minLength={12}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="rounded border bg-white px-3 py-2"
                />
                <input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your Super Admin password"
                  value={actorPassword}
                  onChange={(event) => setActorPassword(event.target.value)}
                  className="rounded border bg-white px-3 py-2"
                />
              </div>
              <button
                type="button"
                disabled={resettingId === admin.id}
                onClick={() => resetPassword(admin)}
                className="mt-3 rounded bg-amber-700 px-4 py-2 font-medium text-white disabled:opacity-50">
                {resettingId === admin.id ? 'Resetting…' : 'Confirm password reset'}
              </button>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {capabilities.map((capability) => (
              <label key={capability} className="flex items-center gap-2 rounded border p-3">
                <input
                  type="checkbox"
                  checked={(admin.assignedCapabilities || []).includes(capability)}
                  onChange={() => toggle(admin.id, capability)}
                />
                <span>{capabilityLabel(capability)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminManagement;
